import { useCallback, useEffect, useRef } from 'react';

import { getUnreadCommentsIntersectionObserver } from '../UnreadCommentsIntersectionObserver.ts';
import { useUnreadCommentsActions } from './useUnreadComments.ts';
import { useDebouncedMarkCommentsAsRead } from './useDebouncedMarkCommentsAsRead.ts';
import { useViewedComments } from './useViewedComments.ts';

const MARK_COMMENTS_AS_READ_DEBOUNCE_MS = 1000;

interface UseUnreadCommentsObserverSetupProps {
	pageId: string | undefined;
	onCommentRead?: (commentIds: string[]) => void;
}

export const useUnreadCommentsObserverSetup = ({
	pageId,
	onCommentRead,
}: UseUnreadCommentsObserverSetupProps): void => {
	const { updateUnreadCommentIDSet } = useUnreadCommentsActions();

	const { debouncedMarkCommentsAsRead, flushPendingComments } = useDebouncedMarkCommentsAsRead(
		MARK_COMMENTS_AS_READ_DEBOUNCE_MS,
	);

	const { updateViewedCommentsCache } = useViewedComments({ pageId: pageId || '' });

	const intersectionCallback = useCallback(
		(commentId: string) => {
			if (commentId) {
				updateUnreadCommentIDSet(new Set([commentId]), 'remove');
				debouncedMarkCommentsAsRead(commentId);
				updateViewedCommentsCache([commentId]);
				// Notify external systems about the comment being read (e.g. content types, whiteboards, databases)
				onCommentRead?.([commentId]);
			}
		},
		[
			updateUnreadCommentIDSet,
			debouncedMarkCommentsAsRead,
			updateViewedCommentsCache,
			onCommentRead,
		],
	);

	// Create refs to avoid stale closures in useEffect
	const intersectionCallbackRef = useRef(intersectionCallback);
	intersectionCallbackRef.current = intersectionCallback;

	const flushPendingCommentsRef = useRef(flushPendingComments);
	flushPendingCommentsRef.current = flushPendingComments;

	useEffect(() => {
		if (!pageId) {
			return;
		}

		const observer = getUnreadCommentsIntersectionObserver();

		observer.registerIntersectionCallback((commentId: string) => {
			intersectionCallbackRef.current(commentId);
		});

		return () => {
			observer.disconnect();
			flushPendingCommentsRef.current();
		};
	}, [pageId]);
};
